import React, { useState, useEffect, useCallback } from 'react';
import { Plug, RefreshCw, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { unicontaOwnerAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';

// Firm-level Uniconta onboarding. One server-user login covers every company the accountant
// manages, so it is entered ONCE here and stored against the user — not copied onto each company
// card. Company cards resolve it at call time, and any company that already carries its own
// credentials keeps using those unchanged.
export function UnicontaOnboarding() {
    const [status, setStatus] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState('');

    const [companies, setCompanies] = useState(null);
    const [skippedInactive, setSkippedInactive] = useState(0);
    const [selected, setSelected] = useState({});      // company_id -> true
    const [journals, setJournals] = useState({});      // company_id -> {loading, list, chosen}

    const loadStatus = useCallback(async () => {
        try {
            const r = await unicontaOwnerAPI.status();
            setStatus(r.data);
            if (r.data?.username) setUsername(r.data.username);
        } catch { setStatus({ configured: false }); }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const saveCredentials = async () => {
        if (!username.trim() || !password) {
            toast.error('Udfyld både brugernavn og adgangskode.');
            return;
        }
        setBusy('save');
        try {
            await unicontaOwnerAPI.saveCredentials(username.trim(), password);
            setPassword('');
            toast.success('Login gemt og bekræftet mod Uniconta.');
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke gemme loginnet');
        } finally { setBusy(''); }
    };

    const removeCredentials = async () => {
        if (!window.confirm('Fjern det gemte Uniconta-login? Virksomheder med deres eget login påvirkes ikke.')) return;
        setBusy('remove');
        try {
            await unicontaOwnerAPI.deleteCredentials();
            setCompanies(null); setSelected({}); setJournals({});
            toast.success('Login fjernet.');
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke fjerne loginnet');
        } finally { setBusy(''); }
    };

    const fetchCompanies = async () => {
        setBusy('companies');
        try {
            const r = await unicontaOwnerAPI.companies();
            setCompanies(r.data?.companies || []);
            setSkippedInactive(r.data?.skipped_inactive || 0);
            setSelected({});
            setJournals({});
            if ((r.data?.companies || []).length === 0) {
                toast.info('Ingen aktive virksomheder fundet på dette login.');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke hente virksomheder');
        } finally { setBusy(''); }
    };

    const toggle = async (c) => {
        const on = !selected[c.company_id];
        setSelected((s) => ({ ...s, [c.company_id]: on }));
        if (on && !journals[c.company_id]) {
            setJournals((j) => ({ ...j, [c.company_id]: { loading: true, list: [], chosen: '' } }));
            try {
                const r = await unicontaOwnerAPI.journals(c.company_id);
                const list = r.data?.journals || [];
                const firstUsable = list.find((x) => !x.blocked);
                setJournals((j) => ({
                    ...j,
                    [c.company_id]: { loading: false, list, chosen: firstUsable ? firstUsable.journal : '' },
                }));
            } catch (e) {
                setJournals((j) => ({ ...j, [c.company_id]: { loading: false, list: [], chosen: '', error: true } }));
                toast.error(`Kunne ikke hente kladder for ${c.name}`);
            }
        }
    };

    const createCards = async () => {
        const picked = (companies || []).filter((c) => selected[c.company_id]);
        if (picked.length === 0) { toast.error('Vælg mindst én virksomhed.'); return; }
        const payload = picked.map((c) => ({
            company_id: c.company_id,
            name: c.name,
            journal: journals[c.company_id]?.chosen || '',
            integration_pack: c.integration_pack,
        }));
        setBusy('create');
        try {
            const r = await unicontaOwnerAPI.selectCompanies(payload);
            const { created_count, updated_count } = r.data || {};
            toast.success(`${created_count} virksomhed(er) oprettet, ${updated_count} opdateret.`);
            await fetchCompanies();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke oprette virksomhederne');
        } finally { setBusy(''); }
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;

    return (
        <Card data-testid="uniconta-onboarding">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Plug className="h-4 w-4" /> Uniconta — tilslut dine virksomheder
                </CardTitle>
                <CardDescription>
                    Indtast dit Uniconta server-bruger login én gang. Det dækker alle de virksomheder,
                    brugeren har adgang til — du behøver ikke indtaste det på hvert virksomhedskort.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* 1. Credentials, once */}
                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label className="text-xs text-muted-foreground">Server-bruger</Label>
                        <Input className="h-9 mt-1" autoComplete="off" value={username}
                               onChange={(e) => setUsername(e.target.value)}
                               data-testid="uni-onboard-user" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Adgangskode</Label>
                        <Input className="h-9 mt-1" type="password" autoComplete="new-password"
                               placeholder={status?.configured ? '•••••••• (gemt)' : ''}
                               value={password} onChange={(e) => setPassword(e.target.value)}
                               data-testid="uni-onboard-pass" />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={saveCredentials} disabled={!!busy}
                            data-testid="uni-onboard-save">
                        {busy === 'save' ? 'Gemmer…' : (status?.configured ? 'Opdater login' : 'Gem login')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={fetchCompanies}
                            disabled={!!busy || !status?.configured} data-testid="uni-onboard-fetch">
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${busy === 'companies' ? 'animate-spin' : ''}`} />
                        Hent virksomheder
                    </Button>
                    {status?.configured && (
                        <Button size="sm" variant="ghost" onClick={removeCredentials} disabled={!!busy}
                                className="text-destructive hover:text-destructive"
                                data-testid="uni-onboard-remove">
                            <Trash2 className="h-4 w-4 mr-1.5" /> Fjern login
                        </Button>
                    )}
                    {status?.configured && (
                        <span className="text-[11px] text-muted-foreground ml-auto inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[#0F766E]" />
                            Login gemt{status.last_verified_at ? ` · bekræftet ${String(status.last_verified_at).slice(0, 10)}` : ''}
                        </span>
                    )}
                </div>

                {/* 2. Company list */}
                {companies && (
                    <div className="pt-2 border-t space-y-2" data-testid="uni-onboard-companies">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium">
                                {companies.length} virksomhed(er) tilgængelige
                            </p>
                            {skippedInactive > 0 && (
                                <span className="text-[11px] text-muted-foreground">
                                    {skippedInactive} inaktive/slettede skjult
                                </span>
                            )}
                        </div>

                        {companies.map((c) => {
                            const j = journals[c.company_id];
                            const managed = c.already_managed;
                            return (
                                <div key={c.company_id} className="rounded-lg border p-3"
                                     data-testid={`uni-onboard-company-${c.company_id}`}>
                                    <div className="flex items-start gap-3">
                                        <input type="checkbox" className="mt-1"
                                               checked={!!selected[c.company_id]}
                                               onChange={() => toggle(c)}
                                               data-testid={`uni-onboard-pick-${c.company_id}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-medium text-sm">{c.name}</span>
                                                <span className="text-[11px] text-muted-foreground font-mono">#{c.company_id}</span>
                                                {c.integration_pack === true && (
                                                    <Badge variant="secondary" className="text-[10px]">
                                                        Integrationspakke · direkte bogføring
                                                    </Badge>
                                                )}
                                                {c.integration_pack === false && (
                                                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                                                        Ingen Integrationspakke · CSV + bilag
                                                    </Badge>
                                                )}
                                                {managed && (
                                                    <Badge variant="outline" className="text-[10px]">
                                                        allerede oprettet: {managed.tenant_name}
                                                    </Badge>
                                                )}
                                            </div>

                                            {selected[c.company_id] && (
                                                <div className="mt-2">
                                                    <Label className="text-[11px] text-muted-foreground">Kladde</Label>
                                                    {j?.loading ? (
                                                        <p className="text-[11px] text-muted-foreground mt-1">Henter kladder…</p>
                                                    ) : j?.list?.length ? (
                                                        <select
                                                            className="mt-1 w-full h-9 rounded-lg border border-input bg-background px-3 text-sm"
                                                            value={j.chosen}
                                                            onChange={(e) => setJournals((jj) => ({
                                                                ...jj, [c.company_id]: { ...jj[c.company_id], chosen: e.target.value } }))}
                                                            data-testid={`uni-onboard-journal-${c.company_id}`}>
                                                            <option value="">— vælg kladde —</option>
                                                            {j.list.map((x) => (
                                                                <option key={x.journal} value={x.journal} disabled={x.blocked}>
                                                                    {x.name}{x.blocked ? ' (spærret — kan ikke bogføres i)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <p className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1">
                                                            <AlertTriangle className="h-3.5 w-3.5" />
                                                            Ingen kladder kunne hentes — indtast navnet manuelt på virksomhedskortet.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" onClick={createCards} disabled={!!busy || selectedCount === 0}
                                    data-testid="uni-onboard-create">
                                {busy === 'create' ? 'Opretter…' : `Opret ${selectedCount || ''} virksomhed(er)`}
                            </Button>
                            <span className="text-[11px] text-muted-foreground">
                                Leveringsmåde sættes automatisk: direkte bogføring hvor Integrationspakken findes, ellers CSV + bilag.
                            </span>
                        </div>
                    </div>
                )}

                <p className="text-[11px] text-muted-foreground pt-2 border-t">
                    Kan en virksomhed eller kladde ikke hentes, kan alt stadig indtastes manuelt på det
                    enkelte virksomhedskort. Virksomheder med deres eget login bliver ikke ændret.
                </p>
            </CardContent>
        </Card>
    );
}

export default UnicontaOnboarding;
